import { useEffect, useRef } from "react";
import { EntitySaveCoordinator } from "./EntitySaveCoordinator";
import { coordinatorRegistry } from "./registry";
import type { CoordinatorConfig } from "./types";

/**
 * Creates an EntitySaveCoordinator for the given entityId and publishes it
 * to the module-level registry so child useEditor hooks find it on the same
 * first render. Call once per editing page (e.g., wizard root).
 *
 * The coordinator instance is held in a ref keyed by entityId, so it
 * survives React.StrictMode's simulated unmount/remount with stable
 * identity. The render-phase publication is idempotent — it only calls
 * `register` (and therefore notifies subscribers) when the registry's value
 * for this entityId would actually change. The effect cleanup only
 * unregisters from the singleton; it does NOT null the coordinator ref.
 * The effect setup re-registers the same coordinator on remount.
 *
 * The previous shape (`ensureCoordinator()` in render + cleanup nulling the
 * ref + setup recreating) churned coordinator identity across the StrictMode
 * cycle. The transient empty registry triggered useEditor's conditional
 * throw between its two useSyncExternalStore calls, corrupted React's hook
 * count, and produced insertBefore/removeChild commit-phase storms on Vite
 * full-reload.
 *
 * The save and onError callbacks are kept up-to-date via refs — callers
 * don't need to memoize them.
 */
export function useCreateEditor<
  TState extends Record<string, unknown>,
  TFields extends Record<string, unknown> = Partial<TState>,
>(entityId: string, config: CoordinatorConfig<TState, TFields>): void {
  const configRef = useRef(config);
  configRef.current = config;

  const coordinatorRef = useRef<{
    id: string;
    coord: EntitySaveCoordinator<TState, TFields>;
  } | null>(null);

  if (
    coordinatorRef.current === null ||
    coordinatorRef.current.id !== entityId
  ) {
    coordinatorRef.current = {
      id: entityId,
      coord: new EntitySaveCoordinator({
        ...config,
        save: (fields) => configRef.current.save(fields),
        onError: (error) => configRef.current.onError?.(error),
      }),
    };
  }
  const coordinator = coordinatorRef.current.coord;

  // Idempotent render-phase publication. The first render registers; later
  // renders no-op because the registry already maps entityId to this same
  // instance. Necessary in render-phase so child useEditor finds the
  // coordinator on its same first render.
  if (coordinatorRegistry.get(entityId) !== coordinator) {
    coordinatorRegistry.register(entityId, coordinator);
  }

  useEffect(() => {
    // Re-publish after a StrictMode simulated unmount cleanup unregistered
    // us. The same coordinator instance is re-registered, so subscribers
    // see identity stability across the cycle.
    if (coordinatorRegistry.get(entityId) !== coordinator) {
      coordinatorRegistry.register(entityId, coordinator);
    }
    return () => {
      coordinatorRegistry.unregister(entityId);
    };
  }, [entityId, coordinator]);

  useEffect(() => {
    coordinator.setServerState(config.initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinator, config.initialState]);
}
